import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Star, MapPin, Camera, Trash2 } from 'lucide-react';
import ClientPageLayout from '../../components/shared/layouts/ClientPageLayout';
import { ClientEmptyState } from '../../components/client/guidance';
import { CardGridSkeleton } from '../../components/shared/ClientPageSkeleton';
import { useFavoriteVendors, useRemoveFavorite } from '../../services/favoriteVendorService';
import { useToastContext } from '../../contexts/ToastContext';

const FAVORITES_SUBTITLE = 'Saved vendors you can revisit, compare, and book when ready.';

const ClientFavorites = () => {
 const toast = useToastContext();
 
 // Fetch favorite vendors from API
 const { data: favoritesResponse, isLoading, error } = useFavoriteVendors();
 
 // Remove favorite mutation
 const removeFavoriteMutation = useRemoveFavorite({
 onSuccess: () => {
 toast.success('Removed from Favorites', 'Vendor has been removed from your favorites.');
 },
 onError: (error) => {
 const errorMessage = error?.response?.data?.message || 'Failed to remove from favorites';
 toast.error('Error', errorMessage);
 }
 });

 // Extract favorites from paginated response
 const favorites = favoritesResponse?.data?.data || favoritesResponse?.data || [];

 const handleRemoveFavorite = (favoriteId, vendorId) => {
 removeFavoriteMutation.mutate({ favoriteId, vendorId });
 };

 // Helper function to get location string
 const getLocation = (vendor) => {
 const parts = [];
 if (vendor?.city) parts.push(vendor.city);
 if (vendor?.state) parts.push(vendor.state);
 if (vendor?.country && !parts.includes(vendor.country)) parts.push(vendor.country);
 return parts.length > 0 ? parts.join(', ') : 'Location not available';
 };

 // Helper function to get first service name
 const getFirstServiceName = (vendor) => {
 const services = vendor?.vendorServices || [];
 if (services.length > 0) {
 const service = services[0];
 return service?.serviceCategory?.category_name || service?.name || 'Service';
 }
 return 'Service';
 };

 // Helper function to get starting price
 const getStartingPrice = (vendor) => {
 const services = vendor?.vendorServices || [];
 if (services.length > 0) {
 // Find the minimum base_price
 const prices = services
 .map(s => s.base_price)
 .filter(p => p !== null && p !== undefined);
 if (prices.length > 0) {
 return Math.min(...prices);
 }
 }
 return null;
 };

 // Helper function to get vendor image
 const getVendorImage = (vendor) => {
 return vendor?.image_url || vendor?.user?.profile_image_url || null;
 };

 const layout = {
 title: 'My Favorites',
 subtitle: FAVORITES_SUBTITLE,
 guideKey: 'favorites',
 };

 if (isLoading) {
 return (
 <ClientPageLayout {...layout}>
 <div className="space-y-5">
 <div className="h-5 w-32 bg-gray-200 rounded animate-pulse" />
 <CardGridSkeleton count={4} />
 </div>
 </ClientPageLayout>
 );
 }

 if (error) {
 return (
 <ClientPageLayout {...layout}>
 <div className="rounded-lg border border-red-200/80 bg-red-50 p-8 text-center">
 <p className="font-semibold text-red-800 mb-1">Couldn’t load favorites</p>
 <p className="text-sm text-red-700/80 mb-4">Please try again.</p>
 <button
 type="button"
 onClick={() => window.location.reload()}
 className="inline-flex items-center justify-center px-5 py-2.5 bg-ink text-white text-sm font-medium rounded-md hover:bg-black transition-colors"
 >
 Retry
 </button>
 </div>
 </ClientPageLayout>
 );
 }

 return (
 <ClientPageLayout {...layout}>
 <div className="space-y-5">
 {favorites.length === 0 ? (
 <ClientEmptyState
 icon={Heart}
 title="No favorites yet"
 description="Save vendors while browsing so you can compare and book them later."
 actionLabel="Browse vendors"
 actionTo="/browse-vendors"
 tips={['Tap the heart on a vendor profile', 'Come back here to book when ready']}
 />
 ) : (
 <>
 <div className="flex items-baseline justify-between gap-3 px-0.5">
 <h2 className="text-sm font-semibold text-ink">
 Your favorites
 <span className="ml-1.5 font-normal text-ink-tertiary">
 ({favorites.length})
 </span>
 </h2>
 </div>

 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 {favorites.map((favorite) => {
 const vendor = favorite.vendor || {};
 const vendorId = vendor.id || favorite.vendor_id;
 const favoriteId = favorite.id;
 const vendorImage = getVendorImage(vendor);
 const location = getLocation(vendor);
 const serviceName = getFirstServiceName(vendor);
 const startingPrice = getStartingPrice(vendor);
 const rating = vendor.average_rating || 0;
 const reviews = vendor.total_reviews || 0;

 return (
 <article
 key={favoriteId}
 className="product-panel p-4 sm:p-5 transition-colors hover:border-ink transition-colors"
 >
 <div className="flex items-start justify-between mb-3">
 <div className="flex items-center space-x-3 flex-1 min-w-0">
 {vendorImage ? (
 <img
 src={vendorImage}
 alt={vendor.business_name || 'Vendor'}
 className="w-12 h-12 rounded-xl object-cover flex-shrink-0"
 onError={(e) => {
 e.target.style.display = 'none';
 e.target.nextSibling.style.display = 'flex';
 }}
 />
 ) : null}
 <div
 className={`w-12 h-12 bg-surface-muted border border-border rounded-xl flex items-center justify-center flex-shrink-0 ${vendorImage ? 'hidden' : ''}`}
 >
 <Camera className="w-6 h-6 text-ink" />
 </div>
 <div className="flex-1 min-w-0">
 <h3 className="font-semibold text-base text-ink truncate">
 {vendor.business_name || 'Vendor'}
 </h3>
 <p className="text-sm text-ink-secondary truncate">
 {serviceName}
 </p>
 </div>
 </div>
 <button
 type="button"
 onClick={() => handleRemoveFavorite(favoriteId, vendorId)}
 disabled={removeFavoriteMutation.isPending}
 className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
 title="Remove from favorites"
 >
 <Trash2 className="w-5 h-5" />
 </button>
 </div>

 <div className="space-y-2 mb-4">
 {rating > 0 && (
 <div className="flex items-center text-sm text-ink-secondary">
 <Star className="w-4 h-4 text-amber-400 fill-amber-400 mr-1" />
 <span className="font-semibold text-ink mr-1">
 {Number(rating).toFixed(1)}
 </span>
 <span>({reviews} {reviews === 1 ? 'review' : 'reviews'})</span>
 </div>
 )}
 <div className="flex items-center text-sm text-ink-secondary">
 <MapPin className="w-4 h-4 text-ink mr-1 flex-shrink-0" />
 <span>{location}</span>
 </div>
 {startingPrice !== null && (
 <div className="text-base font-bold text-ink">
 Starting from ${Number(startingPrice).toLocaleString()}
 </div>
 )}
 </div>

 <div className="flex gap-2">
 <Link
 to={`/vendor/${vendorId}`}
 className="flex-1 bg-ink text-white text-center py-2.5 rounded-md hover:bg-black transition-colors text-sm font-medium"
 >
 View Details
 </Link>
 <Link
 to={`/vendor/${vendorId}/services`}
 className="flex-1 border border-border text-ink text-center py-2.5 rounded-md hover:bg-surface-muted transition-colors text-sm font-medium"
 >
 Book Now
 </Link>
 </div>
 </article>
 );
 })}
 </div>
 </>
 )}
 </div>
 </ClientPageLayout>
 );
};

export default ClientFavorites;
